import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import trainingApi from '../../services/trainingApi';
import api from '../../services/api';

const TrainingScenarioAdmin = ({ token }) => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [platformGroup, setPlatformGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters
  const [orgFilter, setOrgFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [accessFilter, setAccessFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Dialogs
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [shareTargets, setShareTargets] = useState([]);
  const [cloneTarget, setCloneTarget] = useState('');
  const [cloneIncludePersona, setCloneIncludePersona] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit: 500 };
      if (orgFilter) params.organisationId = orgFilter;
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;

      const [scenariosRes, orgsRes] = await Promise.all([
        trainingApi.listScenarios(params),
        trainingApi.listOrganisations(),
      ]);

      const sBody = scenariosRes.data || scenariosRes;
      const sData = sBody.data || sBody;
      setScenarios(sData.scenarios || sData || []);

      const oBody = orgsRes.data || orgsRes;
      const oData = oBody.data || oBody;
      setOrgs(oData.organisations || oData || []);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [orgFilter, statusFilter, searchQuery]);

  useEffect(() => { loadData(); }, [loadData]);

  // Resolve the platform-authoring service group so the Organisation column
  // can render a "Platform Library" badge for platform-canonical scenarios.
  // The /training/organisations endpoint filters to trainingConfig.enabled
  // groups, which doesn't include the platform-authoring group, so we fetch
  // service groups separately.
  useEffect(() => {
    let cancelled = false;
    api.get('/service-groups?includeArchived=false')
      .then(response => {
        if (cancelled) return;
        const body = response.data || response;
        const groups = body.data || body || [];
        const list = Array.isArray(groups) ? groups : [];
        const platform = list.find(g => g.groupType === 'platform_authoring');
        if (platform) setPlatformGroup(platform);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isPlatformId = (orgId) => {
    if (!orgId || !platformGroup) return false;
    const id = typeof orgId === 'object' ? orgId._id : orgId;
    return id === platformGroup._id || id?.toString() === platformGroup._id;
  };

  const getOrgName = (orgId) => {
    if (!orgId) return 'Global (no org)';
    if (isPlatformId(orgId)) return platformGroup?.name || 'Platform Library';
    const org = orgs.find(o => o._id === orgId || o._id === orgId?.toString());
    return org?.name || orgId;
  };

  const handleAccessChange = async (scenarioId, newAccess) => {
    try {
      setError(null);
      await trainingApi.updateScenario(scenarioId, { studentAccess: newAccess });
      setSuccess('Access updated');
      setTimeout(() => setSuccess(null), 2000);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update access');
    }
  };

  const openShare = (scenario) => {
    setSelectedScenario(scenario);
    setShareTargets([]);
    setError(null);
    setShareDialogOpen(true);
  };

  const handleShare = async () => {
    if (!selectedScenario || shareTargets.length === 0) return;
    try {
      setSaving(true);
      setError(null);
      await trainingApi.shareScenario(selectedScenario._id, shareTargets);
      setShareDialogOpen(false);
      setSuccess('Scenario shared successfully');
      setTimeout(() => setSuccess(null), 3000);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to share scenario');
    } finally {
      setSaving(false);
    }
  };

  const handleUnshare = async (scenarioId, targetOrgId) => {
    try {
      setError(null);
      await trainingApi.unshareScenario(scenarioId, targetOrgId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unshare');
    }
  };

  const openClone = (scenario) => {
    setSelectedScenario(scenario);
    setCloneTarget('');
    setCloneIncludePersona(true);
    setError(null);
    setCloneDialogOpen(true);
  };

  const handleClone = async () => {
    if (!selectedScenario || !cloneTarget) return;
    try {
      setSaving(true);
      setError(null);
      await trainingApi.cloneScenario(selectedScenario._id, cloneTarget, cloneIncludePersona);
      setCloneDialogOpen(false);
      setSuccess('Scenario cloned successfully (created as draft)');
      setTimeout(() => setSuccess(null), 3000);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clone scenario');
    } finally {
      setSaving(false);
    }
  };

  const filteredScenarios = scenarios.filter(s => {
    if (accessFilter && (s.studentAccess || 'hidden') !== accessFilter) return false;
    return true;
  });

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Training Scenario Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Manage scenarios across all training organisations. Control student access, share scenarios between organisations, and clone scenarios to new organisations.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AutoFixHighIcon />}
            onClick={() => navigate('/training/scenarios/wizard', {
              state: { from: '/admin/dashboard/training-scenarios' },
            })}
          >
            Wizard
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/training/scenarios/new', {
              state: { from: '/admin/dashboard/training-scenarios' },
            })}
          >
            Create Scenario
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small" select label="Organisation" value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value)} sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All Organisations</MenuItem>
          {platformGroup && (
            <MenuItem value={platformGroup._id}>
              {platformGroup.name} (Platform Library)
            </MenuItem>
          )}
          {orgs.map(o => <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>)}
        </TextField>
        <TextField
          size="small" select label="Status" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="published">Published</MenuItem>
          <MenuItem value="archived">Archived</MenuItem>
        </TextField>
        <TextField
          size="small" select label="Student Access" value={accessFilter}
          onChange={(e) => setAccessFilter(e.target.value)} sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All Access Levels</MenuItem>
          <MenuItem value="library">Library</MenuItem>
          <MenuItem value="assignment_only">Assignment Only</MenuItem>
          <MenuItem value="hidden">Hidden</MenuItem>
        </TextField>
        <TextField
          size="small" placeholder="Search scenarios..." value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredScenarios.length} scenario{filteredScenarios.length !== 1 ? 's' : ''}
        </Typography>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : filteredScenarios.length === 0 ? (
        <Alert severity="info">No scenarios found matching your filters.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Student Access</TableCell>
                <TableCell>Shared With</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredScenarios.map(s => (
                <TableRow key={s._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{s.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.slug}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{getOrgName(s.institutionGroupId)}</Typography>
                      {isPlatformId(s.institutionGroupId) && (
                        <Chip label="Platform Library" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {(s.category || '').replace(/_/g, ' ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={s.difficulty || '—'} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={s.status || 'draft'}
                      color={s.status === 'published' ? 'success' : s.status === 'archived' ? 'default' : 'warning'}
                      variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select size="small" variant="standard"
                      value={s.studentAccess || 'hidden'}
                      onChange={(e) => handleAccessChange(s._id, e.target.value)}
                      sx={{ minWidth: 130 }}
                    >
                      <MenuItem value="library">Library</MenuItem>
                      <MenuItem value="assignment_only">Assignment Only</MenuItem>
                      <MenuItem value="hidden">Hidden</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    {(s.sharedWithOrgs || []).length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(s.sharedWithOrgs || []).map(oid => (
                          <Chip key={oid} size="small" label={getOrgName(oid)}
                            onDelete={() => handleUnshare(s._id, oid)}
                            variant="outlined" color="info" />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">None</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit scenario">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/training/scenarios/${s._id}/edit`, {
                          state: { from: '/admin/dashboard/training-scenarios' },
                        })}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Share with organisations">
                      <IconButton size="small" onClick={() => openShare(s)}><ShareIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Clone to another organisation">
                      <IconButton size="small" onClick={() => openClone(s)}><ContentCopyIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share Scenario</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share "<strong>{selectedScenario?.name}</strong>" with other training organisations.
            They will see it in their scenario library but cannot edit it.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Owner: {getOrgName(selectedScenario?.institutionGroupId)}
          </Typography>
          {orgs.length === 0 ? (
            <Alert severity="info">No training organisations available.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {orgs.filter(o => o._id !== selectedScenario?.institutionGroupId).map(o => {
                const alreadyShared = (selectedScenario?.sharedWithOrgs || []).some(
                  sid => sid === o._id || sid?.toString() === o._id
                );
                return (
                  <Box key={o._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, bgcolor: alreadyShared ? 'success.50' : 'transparent' }}>
                    <Switch
                      size="small"
                      checked={alreadyShared || shareTargets.includes(o._id)}
                      onChange={(e) => {
                        if (alreadyShared) return;
                        if (e.target.checked) setShareTargets(prev => [...prev, o._id]);
                        else setShareTargets(prev => prev.filter(id => id !== o._id));
                      }}
                      disabled={alreadyShared}
                    />
                    <Typography variant="body2">{o.name}</Typography>
                    {alreadyShared && <Chip size="small" label="Already shared" color="success" variant="outlined" />}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleShare} disabled={saving || shareTargets.length === 0}>
            {saving ? 'Sharing...' : `Share with ${shareTargets.length} org${shareTargets.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Dialog */}
      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clone Scenario to Organisation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a full copy of "<strong>{selectedScenario?.name}</strong>" in another organisation.
            The clone will be created as a <strong>draft</strong> with <strong>hidden</strong> student access,
            so the receiving organisation can review and customise it before publishing.
          </Typography>
          <TextField
            select fullWidth label="Target Organisation"
            value={cloneTarget} onChange={(e) => setCloneTarget(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          >
            {orgs.map(o => <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Switch checked={cloneIncludePersona} onChange={(e) => setCloneIncludePersona(e.target.checked)} />}
            label="Also clone the patient persona (recommended)"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            If unchecked, the clone will reference the original persona. This means changes to the original persona will affect the cloned scenario.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleClone} disabled={saving || !cloneTarget}>
            {saving ? 'Cloning...' : 'Clone Scenario'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingScenarioAdmin;
